const normalizeRateClass = (value) => (value || '').trim().toUpperCase();
const toRateOrNull = (rate) => (rate === '' || rate === null || rate === undefined ? null : Number(rate));

export const isRateClassCell = (row, { rate_class, min, subregion_id, service_level_id }) =>
    normalizeRateClass(row?.dimensions?.rate_class) === normalizeRateClass(rate_class) &&
    (subregion_id ? row.subregion_id === subregion_id : row.min === min) &&
    (service_level_id ? row.service_level_id === service_level_id : !row.service_level_id);

export const upsertRateClassRate = (rows = [], { rate_class, min, max, subregion_id, service_level_id, rate }) => {
    const idx = rows.findIndex((row) => isRateClassCell(row, { rate_class, min, subregion_id, service_level_id }));
    if (idx >= 0) {
        return rows.map((row, i) => (i === idx ? { ...row, rate: toRateOrNull(rate) } : row));
    }
    return [
        ...rows,
        {
            min,
            max,
            min2: 0,
            max2: 'infinity',
            ...(subregion_id ? { subregion_id } : {}),
            ...(service_level_id ? { service_level_id } : {}),
            dimensions: { rate_class: normalizeRateClass(rate_class) },
            qbo_tag: rows[0]?.qbo_tag || '',
            rate: toRateOrNull(rate),
        },
    ];
};

/**
 * Writes to a service level's row in a type that holds one row per level, e.g.
 * CUSTOM_SERVICE_LEVEL_STOP_ADJUSTMENT, or Labor with its "after N minutes" `min`. Adds the row if the level
 * has none.
 * @param {Object[]} rows
 * @param {{ service_level_id: string, rate?: number, min?: number }} patch - The fields to set on that row.
 * @returns {Object[]}
 */
export const upsertServiceLevelRate = (rows = [], { service_level_id, ...patch }) => {
    const idx = rows.findIndex((row) => row.service_level_id === service_level_id);
    if (idx >= 0) {
        return rows.map((row, i) => (i === idx ? { ...row, ...patch } : row));
    }
    return [...rows, { min: 0, max: 'infinity', service_level_id, ...patch }];
};

// With service_level_id, tags only that level's rows (a shared type prefix, e.g.
// CUSTOM_SERVICE_LEVEL_ITEM_RATE, otherwise cross-contaminates every custom level).
// Without it, tags every row, unchanged from the type-wide behavior.
export const applyQboTag = (rows = [], { qbo, service_level_id }) =>
    rows.map((row) => (service_level_id && row.service_level_id !== service_level_id ? row : { ...row, qbo_tag: qbo }));

/**
 * Merges new rows into a shared CUSTOM_SERVICE_LEVEL_* type, which holds every custom level's rows. Each
 * level in `serviceLevelIds` or in `nextRows` ends up with only its `nextRows`; other levels keep theirs.
 * @param {Object[]} prevRows
 * @param {Object[]} nextRows
 * @param {string[]} [serviceLevelIds] - Levels to clear even when `nextRows` has none of their rows.
 * @returns {Object[]}
 */
export const replaceServiceLevelRows = (prevRows = [], nextRows = [], serviceLevelIds = []) => {
    const replaced = new Set([...serviceLevelIds, ...nextRows.map((row) => row.service_level_id)]);
    return [...prevRows.filter((row) => !replaced.has(row.service_level_id)), ...nextRows];
};
